"use client";
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { urlFor } from "@/sanity/lib/image";
import Link from "next/link";
import Image from "next/image";

type Project = {
  _key?: string;
  _id?: string;
  projectTitle?: string;
  title?: string;
  name?: string;
  subtitle?: string;
  industry?: string;
  client?: {
    industry?: {
      title?: string;
    };
  };
  category?: {
    title?: string;
  };
  projectOverview?: string;
  description?: string;
  excerpt?: string;
  featuredImage?: any;
  image?: any;
  services?: any[];
  serviceBadges?: string[];
  features?: string[];
  tags?: string[];
  technologies?: string[];
  slug?: string;
  link?: {
    url?: string;
    text?: string;
  };
  buttons?: any[];
};

type Props = {
  tagline?: string;
  title?: string;
  description?: string;
  projects?: Project[];
  contentType?: "projects" | "services" | "custom";
  showTags?: boolean;
  showImages?: boolean;
  cardLayout?: "alternating" | "left" | "right";
  maxItems?: number;
  styling?: {
    backgroundColor?: string;
    padding?: "sm" | "md" | "lg" | "xl";
    maxWidth?: "sm" | "md" | "lg" | "xl" | "container" | "full";
    cardSpacing?: "tight" | "normal" | "loose";
  };
};

export type Portfolio23Props = React.ComponentPropsWithoutRef<"section"> &
  Partial<Props>;

const ProjectCard = ({
  item,
  index,
  showTags = true,
  showImages = true,
  cardLayout = "alternating",
  contentType = "projects",
}: {
  item: Project;
  index: number;
  showTags?: boolean;
  showImages?: boolean;
  cardLayout?: "alternating" | "left" | "right";
  contentType?: "projects" | "services" | "custom";
}) => {
  // Determine image position based on layout
  const getImagePosition = () => {
    if (cardLayout === "left") return "left";
    if (cardLayout === "right") return "right";
    return index % 2 === 0 ? "left" : "right";
  };

  const imagePosition = getImagePosition();
  const isImageLeft = imagePosition === "left";

  // Extract data based on content type
  let title, subtitle, description, image, tags, linkUrl, linkText;

  if (contentType === "custom") {
    title = item.title;
    subtitle = item.subtitle;
    description = item.description;
    image = item.image;
    tags = item.tags;
    linkUrl = item.link?.url;
    linkText = item.link?.text;
  } else if (contentType === "projects") {
    title = item.projectTitle || item.title;
    subtitle = item.client?.industry?.title || "WEBSITE DESIGN";
    description = item.projectOverview || item.description;
    image = item.featuredImage || item.image;
    
    // Handle services with safety checks for references
    const isReference = (obj: any): boolean => {
      return typeof obj === 'object' && obj && '_ref' in obj;
    };
    
    const serviceNames = item.services
      ?.filter((service: any) => service && !isReference(service))
      ?.map((service: any) => service.name)
      ?.filter((name: any): name is string => typeof name === 'string' && name.trim() !== '') || [];
    
    tags = serviceNames.length > 0 ? serviceNames : ["WEB DESIGN"];
    linkUrl = `/work/${item.slug}`;
    linkText = "View Case Study";
  } else if (contentType === "services") {
    title = item.name || item.title;
    subtitle = item.category?.title || "SERVICE";
    description = item.description;
    image = item.image || item.featuredImage;
    tags = item.features || item.serviceBadges || item.tags || [];
    linkUrl = `/services/${item.slug}`;
    linkText = "Learn More";
  } else {
    // Fallback for unknown content types
    title = item.title || item.name;
    subtitle = item.subtitle || item.industry || (typeof item.category === 'string' ? item.category : item.category?.title);
    description = item.description || item.excerpt;
    image = item.image || item.featuredImage;
    tags = item.tags || item.services || item.technologies;
    linkUrl = item.link?.url || `/work/${item.slug}`;
    linkText = item.link?.text || "View Details";
  }

  return (
    <div>
      <Link href={linkUrl || "#"} className="group block">
        <Card className="border border-border overflow-hidden rounded-2xl hover:shadow-xl transition-shadow duration-300">
          <CardContent
            className={cn(
              "p-0 flex flex-col md:flex-row",
              !isImageLeft && "md:flex-row-reverse"
            )}
          >
            {showImages && image && (
              <div className="p-4 rounded-xl relative w-full md:w-2/5 h-64 md:h-auto min-h-[300px]">
                <Image
                  src={urlFor(image).width(360).height(240).url()}
                  alt={image.alt || title || "Card image"}
                  width={360}
                  height={240}
                  sizes="(max-width: 768px) calc(100vw - 3rem), 40vw"
                  loading="lazy"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            )}
            <div className={cn(
              "w-full p-6 md:p-10 flex flex-col justify-center",
              showImages ? "md:w-3/5" : "md:w-full"
            )}>
              {subtitle && (
                <p className="text-sm text-subheading uppercase tracking-wide mb-2">
                  {subtitle}
                </p>
              )}
              <h3 className="text-2xl md:text-3xl font-medium text-heading mb-3">
                {title}
              </h3>
              {description && (
                <p className="text-text leading-relaxed mb-6">
                  {description}
                </p>
              )}
              {showTags && tags && tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {tags.map((tag: any, tagIndex: number) => (
                    <Badge
                      key={typeof tag === 'string' ? tag : tagIndex}
                      variant="default"
                    >
                      {typeof tag === 'string' ? tag : tag.name || tag.title || `Tag ${tagIndex + 1}`}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex items-center text-sm font-medium text-btn-text group-hover:text-text-muted transition-colors mt-auto">
                {linkText} <ArrowRight className="ml-1 w-4 h-4" />
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    </div>
  );
};

export const Portfolio23 = (props: Portfolio23Props) => {
  const { 
    tagline, 
    title, 
    description, 
    projects: manualProjects, 
    contentType = "projects",
    showTags = true,
    showImages = true,
    cardLayout = "alternating",
    maxItems,
    styling = {}
  } = {
    ...Portfolio23Defaults,
    ...props,
  };

  const [items, setItems] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let fetchedItems: Project[] = [];

        const fetchPublicItems = async (type: "allProjects" | "allServices") => {
          const response = await fetch(`/api/public-content?type=${type}`, {
            method: "GET",
            cache: "force-cache",
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch ${type}`);
          }
          return (await response.json()) as Project[];
        };

        if (contentType === "projects") {
          if (manualProjects && manualProjects.length > 0) {
            // Use manually selected projects if provided
            fetchedItems = manualProjects;
          } else {
            // Fetch all projects dynamically
            fetchedItems = await fetchPublicItems("allProjects");
          }
        } else if (contentType === "services") {
          if (manualProjects && manualProjects.length > 0) {
            // Use manually selected services if provided
            fetchedItems = manualProjects;
          } else {
            // Fetch all services dynamically
            fetchedItems = await fetchPublicItems("allServices");
          }
        } else if (contentType === "custom") {
          // Use custom cards if provided
          fetchedItems = manualProjects || [];
        }

        // Apply max items limit
        if (maxItems && maxItems > 0) {
          fetchedItems = fetchedItems.slice(0, maxItems);
        }

        setItems(fetchedItems);
      } catch (error) {
        console.error('Error fetching data:', error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [contentType, manualProjects, maxItems]);

  // Dynamic classes
  const bgClass = styling?.backgroundColor || "";
  const paddingClass = {
    sm: "py-8",
    md: "py-12", 
    lg: "py-16",
    xl: "py-20"
  }[styling?.padding || "lg"];
  
  const maxWidthClass = {
    container: "",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg", 
    xl: "max-w-6xl",
    full: "max-w-full"
  }[styling?.maxWidth || "container"];

  const spacingClass = {
    tight: "space-y-8",
    normal: "space-y-16",
    loose: "space-y-24"
  }[styling?.cardSpacing || "normal"];

  if (loading) {
    return (
      <section className={`${bgClass} ${paddingClass}`}>
        <div className={`container mx-auto px-6 ${maxWidthClass}`}>
          <div className="text-center">
            <p className="text-text">Loading...</p>
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <section className={`${bgClass} ${paddingClass}`}>
      <div className={`container mx-auto px-6 ${maxWidthClass}`}>
        {(tagline || title || description) && (
          <header className="mx-auto mb-12 max-w-lg text-center md:mb-18 lg:mb-20">
            {tagline && <p className="mb-3 font-semibold md:mb-4">{tagline}</p>}
            {title && (
              <h2 className="mb-5 text-5xl font-bold md:mb-6 md:text-7xl lg:text-8xl">
                {title}
              </h2>
            )}
            {description && <p className="md:text-md">{description}</p>}
          </header>
        )}
        
        <div className={spacingClass}>
          {items.map((project, index) => {
            return (
              <div key={project._key || project._id || index}>
                <ProjectCard
                  item={project}
                  index={index}
                  showTags={showTags}
                  showImages={showImages}
                  cardLayout={cardLayout}
                  contentType={contentType}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Portfolio23Defaults: Props = {
  tagline: "Portfolio",
  title: "Short heading goes here",
  description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
  projects: [],
};
